import MenuItem from "@mui/material/MenuItem";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import type { ReactElement } from "react";

import type { DispatchMenuAction, DispatchMenuEntry } from "@/hooks/dispatch/dispatchMenu";
import { zinc } from "@/ui/tokens";
import { DISPATCH_TASK_PALETTE } from "./dispatchColors";

/**
 * The entries of a Dispatch right-click menu, one `MenuItem` each: the acknowledgement entry in white, the task entries
 * in the task orange, each with its key hint on the right, as MUI menus show shortcuts.
 *
 * Returned as an array rather than a component, so each item is a direct child of the MUI `Menu` and its keyboard
 * walk reaches it; a fragment or wrapper would hide the items from it.
 *
 * @param entries - The entries to offer, as `dispatchMenuEntries` lists them.
 * @param onPick - Runs the entry chosen; the caller closes the menu.
 * @param sx - Styles each item takes, as a menu laid out on a grid places them.
 */
export function dispatchMenuItems(
  entries: readonly DispatchMenuEntry[],
  onPick: (action: DispatchMenuAction) => void,
  sx?: SxProps<Theme>,
): ReactElement[] {
  return entries.map(({ action, label, hint }) => {
    const isTask = action.kind === "make" || action.kind === "move";
    return (
      <MenuItem key={`entry:${action.kind}`} onClick={() => onPick(action)} sx={sx}>
        <Typography sx={{ flex: 1, fontSize: "0.8125rem", color: isTask ? DISPATCH_TASK_PALETTE.text : "white" }}>
          {label}
        </Typography>
        {hint && <Typography sx={{ ml: 3, fontSize: "0.75rem", color: zinc[400] }}>{hint}</Typography>}
      </MenuItem>
    );
  });
}
